import { KeyboardEvent, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import OwButton from "../OwButton/OwButton";
import PromoCodeService from "../../../services/PromoCodeService";
import "./OwPromoCodeInput.scss";

type Props = {
    /** Code currently applied to the order / cart. Null or empty renders the entry state. */
    appliedCode?: string | null;
    /** Discount currently applied (positive number), shown as -$x.xx next to the code. */
    appliedDiscount?: number | null;
    /** Called with the normalized (trimmed, upper-cased) code. */
    onApply: (code: string) => void;
    onRemove: () => void;
    /** Translation key for an inline error (e.g. promo_code_error_expired). */
    errorKey?: string | null;
    /** Disables input, Apply and Remove (e.g. while the cart is re-syncing). */
    disabled?: boolean;
    /** A request is in flight: shows a spinner on Apply and disables the controls. */
    applying?: boolean;
    /** When false the applied state is read-only (no Remove) and the entry state is hidden. */
    canEdit?: boolean;
    pricingPrecision?: number;
    /** ISO 4217 code used to format `appliedDiscount` (e.g. the vendor catalog's currency). Defaults to USD. */
    currency?: string;
    className?: string;
    /** Compact single-line layout for the totals panel. */
    isSmall?: boolean;
    /**
     * Start collapsed behind a "Have a promo code?" link and reveal the entry on click (checkout
     * summaries). Opens automatically when an error needs to be shown.
     */
    collapsible?: boolean;
    /**
     * Lay the applied state out as a summary row: code + remove on the left, amount on the right.
     * Default is a right-aligned cluster for the Order Entry totals panel.
     */
    rowLayout?: boolean;
    /** Hide the "Promo Code" text in the applied state when the host already renders its own label. */
    showLabel?: boolean;
};

/**
 * Shared promo code entry + applied-state control used by Order Entry totals and Ecommerce checkout.
 * Transport-free: the parent owns the socket and feeds back `appliedCode` / `errorKey`.
 */
export const OwPromoCodeInput = (props: Props) => {
    const intl = useIntl();
    const canEdit = props.canEdit !== false;
    const precision = props.pricingPrecision || 2;

    const [code, setCode] = useState("");
    const [localErrorKey, setLocalErrorKey] = useState<string | null>(null);
    const [isOpen, setIsOpen] = useState(!props.collapsible);

    // A successful apply (or a remove) resets the draft entry so the next code starts clean, and a
    // collapsible entry folds back up behind its link.
    useEffect(() => {
        setCode("");
        setLocalErrorKey(null);

        if (props.collapsible) {
            setIsOpen(false);
        }
    }, [props.appliedCode, props.collapsible]);

    // An error from the parent must be visible even if the entry was collapsed.
    useEffect(() => {
        if (props.errorKey) {
            setIsOpen(true);
        }
    }, [props.errorKey]);

    const isBusy = !!props.disabled || !!props.applying;
    const errorKey = localErrorKey || props.errorKey || null;

    const currencyFormatter = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: props.currency || "USD",
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
    });

    const apply = () => {
        const normalized = PromoCodeService.normalizeCode(code);

        if (!normalized.length) {
            setLocalErrorKey("required");
            return;
        }

        setLocalErrorKey(null);
        props.onApply(normalized);
    };

    const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.preventDefault();
            e.stopPropagation();

            if (!isBusy) {
                apply();
            }
        }
    };

    const renderError = () => {
        if (!errorKey) {
            return null;
        }

        return (
            <div
                className={"promo-code-error text-danger small mt-1 " + (props.rowLayout ? "text-start" : "text-end")}
                data-testid={"promo-code-error"}
                role={"alert"}>
                {intl.formatMessage({ id: errorKey, defaultMessage: errorKey })}
            </div>
        );
    };

    // While an apply / remove is in flight the whole control is covered by a rounded "please wait"
    // overlay (matching the input group's corners) instead of swapping the Apply button's label,
    // which does not fit the compact button.
    const renderBusyOverlay = () => {
        if (!props.applying) {
            return null;
        }

        return (
            <div className={"promo-code-overlay"} data-testid={"promo-code-overlay"} role={"status"} aria-live={"polite"}>
                <div className={"overlay-content"}>
                    <i className={"fas fa-spinner fa-spin me-2"} aria-hidden={"true"} />
                    {intl.formatMessage({ id: "please_wait", defaultMessage: "Please wait" })}
                </div>
            </div>
        );
    };

    const wrapperClass = "OwPromoCodeInput " + (props.isSmall ? "small-controls " : "") + (props.className || "");

    if (props.appliedCode) {
        const codeAndRemove = (
            <span className={"promo-code-applied-label d-inline-flex align-items-center gap-1"}>
                {props.showLabel !== false ? intl.formatMessage({ id: "promo_code", defaultMessage: "Promo Code" }) : null}
                {/* <code>, not <span>: host totals boxes zero the right padding on their last span. */}
                <code className={"promo-code-badge badge bg-success"} data-testid={"promo-code-applied-code"}>
                    {props.appliedCode}
                </code>
                {canEdit ? (
                    <OwButton
                        className={"btn-outline-danger promo-code-remove"}
                        btnSizeClass={"btn-sm"}
                        centerIconClass={"fas fa-times"}
                        hoverTitle={"remove_promo_code"}
                        ariaLabel={intl.formatMessage({ id: "remove_promo_code", defaultMessage: "Remove Promo Code" })}
                        testId={"promo-code-remove"}
                        disabled={isBusy}
                        fn={() => {
                            props.onRemove();
                        }}
                    />
                ) : null}
            </span>
        );

        const amount =
            props.appliedDiscount !== undefined && props.appliedDiscount !== null ? (
                <span className={"promo-code-applied-amount"} data-testid={"promo-code-applied-amount"}>
                    -{currencyFormatter.format(Math.abs(Number(props.appliedDiscount) || 0))}
                </span>
            ) : null;

        return (
            <div className={wrapperClass + " applied"} data-testid={"promo-code-applied"}>
                <div
                    className={
                        "d-flex align-items-center gap-1 " + (props.rowLayout ? "justify-content-between" : "justify-content-end flex-wrap")
                    }>
                    {codeAndRemove}
                    {amount}
                </div>
                {renderBusyOverlay()}
                {renderError()}
            </div>
        );
    }

    if (!canEdit) {
        return null;
    }

    if (props.collapsible && !isOpen) {
        return (
            <div className={wrapperClass + " collapsed"} data-testid={"promo-code-entry"}>
                <button
                    type={"button"}
                    className={"btn btn-link btn-sm p-0 promo-code-toggle"}
                    data-testid={"promo-code-toggle"}
                    disabled={isBusy}
                    onClick={() => {
                        setIsOpen(true);
                    }}>
                    {intl.formatMessage({ id: "have_a_promo_code", defaultMessage: "Have a promo code?" })}
                </button>
            </div>
        );
    }

    return (
        <div className={wrapperClass} data-testid={"promo-code-entry"}>
            <div className={"input-group " + (props.isSmall ? "input-group-sm" : "")}>
                <input
                    type={"text"}
                    className={"form-control promo-code-input"}
                    name={"promo_code"}
                    data-testid={"promo-code-input"}
                    aria-label={intl.formatMessage({ id: "promo_code", defaultMessage: "Promo Code" })}
                    placeholder={intl.formatMessage({ id: "enter_promo_code", defaultMessage: "Enter promo code" })}
                    value={code}
                    maxLength={64}
                    autoComplete={"off"}
                    autoCapitalize={"characters"}
                    autoFocus={!!props.collapsible}
                    disabled={isBusy}
                    onChange={(e) => {
                        setCode(e.target.value.toUpperCase());

                        if (localErrorKey) {
                            setLocalErrorKey(null);
                        }
                    }}
                    onKeyDown={onKeyDown}
                />
                <OwButton
                    className={"btn-primary promo-code-apply"}
                    title={"apply"}
                    disabled={isBusy || !code.trim().length}
                    testId={"promo-code-apply"}
                    fn={apply}
                />
            </div>
            {renderBusyOverlay()}
            {renderError()}
        </div>
    );
};

export default OwPromoCodeInput;
